import { module } from 'modujs'
import { html } from '../utils/environment'

export default class extends module {
    constructor(m) {
        super(m)

        // UI
        this.$progress  = this.$('progress')[0]
        this.$range     = this.$('range')[0]
        this.$video     = this.$('video')[0]

        // Binding
        this.onVideoTimeUpdateBind  = this.onVideoTimeUpdate.bind(this)
        this.updateUIBind           = this.updateUI.bind(this)
        this.onVideoEndedBind       = this.onVideoEnded.bind(this)
        this.onFullScreenChangeBind = this.onFullScreenChange.bind(this)
        this.onResizeBind           = this.onResize.bind(this)

        // Events
        this.events = {
            click: {
                activate: 'onActivateClick',
                play: 'onPlayClick',
                fullscreen: 'onFullScreenClick',
                mute: 'onMuteClick',
                video: 'onPlayClick',
                cta: 'onPlayClick'
            },
            input: {
                range: 'onRangeChange'
            }
        }

        // Data
        this.isPlaying = false
        this.headerHeight = parseInt(getComputedStyle(html).getPropertyValue('--header-height').replace('px', ''))
        this.eventsRequiringUIUpdate = 'play playing pause ended'.split(' ')
        this.window = {
            width: 0,
            height: 0
        }
    }
    
    ///////////////
    // Lifecyle
    ///////////////

    init() {
        this.bindEvents()
        this.computeMetrics()
        this.computeVideoSrc()
    }

    destroy() {
        super.destroy()
        this.unbindEvents()
    }

    ///////////////
    // Events
    ///////////////
    bindEvents() {
        // Window Resize
        window.addEventListener('resizeEnd', this.onResizeBind)

        // Time update
        this.$video.addEventListener('timeupdate', this.onVideoTimeUpdateBind)
        
        // Any event occuring to player
        for (let event of this.eventsRequiringUIUpdate) {
            this.$video.addEventListener(event, this.updateUIBind);
        }

        // End of video
        this.$video.addEventListener('ended', this.onVideoEndedBind)

        // Full screen
        document.addEventListener('fullscreenchange', this.onFullScreenChangeBind)
    }

    unbindEvents() {
        window.removeEventListener('resizeEnd', this.onResizeBind)

        this.$video.removeEventListener('timeupdate', this.onVideoTimeUpdateBind)

        if (this.eventsRequiringUIUpdate) {
            for(let event of this.eventsRequiringUIUpdate) {
                this.$video?.removeEventListener(event, this.updateUIBind)    
            }
        }

        this.$video.removeEventListener('ended', this.onVideoEndedBind)

        document.removeEventListener('fullscreenchange', this.onFullScreenChangeBind)
    }

    ///////////////
    // Callbacks
    ///////////////
    onResize() {
        this.computeMetrics()
        this.computeVideoSrc()
    }

    onActivateClick() {
        if (this.el.classList.contains('is-active')) return

        const args = {
            target: this.el, 
            options: { 
                offset: -this.headerHeight 
            }
        }

        this.call('scrollTo', args, 'Scroll')
        this.el.classList.add('is-active')
        this.play()
    }

    onPlayClick() {
        this.$video.paused ? this.play() : this.pause()
    }

    onFullScreenClick() { 
        if (!document.fullscreenElement) {
            if (this.el.requestFullscreen) {
                this.el.requestFullscreen()
            } else {
                this.$video.webkitEnterFullScreen()
            }
        } else if (document.exitFullscreen) {
            document.exitFullscreen()
        } 
    }

    onFullScreenChange() {
        this.updateUI()
    }

    onMuteClick() {
        this.$video.muted = !this.$video.muted

        this.updateUI()
    }

    onVideoTimeUpdate() {
        this.updateProgressBar()
    }

    onVideoEnded() {
        this.reset()
    }

    onRangeChange() {
        this.$video.currentTime = this.$range.value * this.$video.duration
    }

    ///////////////
    // Methods
    ///////////////
    computeMetrics() {
        this.window = {
            width: window.innerWidth,
            height: window.innerHeight
        }
    }

    computeVideoSrc() {
        // Vertical video is optionnal
        if (this.getData('vertical') == null) return

        // Check window ratio and get correct video src
        const src = this.window.width > this.window.height ? this.getData('horizontal') : this.getData('vertical')

        // If same src, do nothing
        if (this.$video.src == src) return
        
        // Replace src
        this.$video.src = src

        // If the video was playing, re-play it
        if (this.isPlaying) this.play()
    }

    play() {
        this.$video.play()
        this.isPlaying = true
        this.updateUI()
    }

    pause() {
        this.$video.pause()
        this.isPlaying = false
        this.updateUI()
    }

    updateProgressBar() {
        if (this.$video.duration) {
            this.$range.value = this.$video.currentTime / this.$video.duration
        } else {
            this.$range.value = 0
        }

        this.$progress.style.setProperty('--progress', this.$range.value)
    }

    updateUI() {
        this.updateProgressBar()

        this.el.classList.toggle('is-playing', !this.$video.paused)
        this.el.classList.toggle('is-muted', this.$video.muted)
        this.el.classList.toggle('is-fullscreen', document.fullscreenElement == this.el)
    }

    reset() {
        this.el.classList.remove('is-active')
        this.$video.autoplay = false
        this.$video.load()
        this.updateUI()
    }
}
